import React, { useState, useCallback, useEffect, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Typography, Divider, TextField } from "@mui/material";
import axios from "axios";
import {
  getMainModelDataApi,
  getMainModelHistoryApi,
  getBackgroundModelUrlApi,
  getBackgroundModelHistoryApi,
  updateMainModelApi,
  updateBackgroundModelApi,
  deleteBackgroundModelApi,
  deleteMainModelApi,
  getModelSASApi,
  updateLargeMainModelApi,
  updateLargeBackgroundModelApi
} from "../../../api/model";
import { getFileSize } from "../../../utils/file.js";
import { parseIfcFile, validateModel } from "../../../utils/ifcHelper.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import Popup from "../../../component/popup/Popup.js";
import Table from "../../../component/table/MainTable.js";
import NotificationPopup from "../../../component/popup/NotificationPopup.js";
import FileList from "../../../component/FileList.js";
import UploadNewFileButton from "../../../component/button/UploadNewFileButton.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const ManageModelPopup = ({ open, onClose, onUploadSuccess, user, qtoIds }) => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const buttonStyle = {
    pr: 1.5,
    pl: 1,
    py: 0,
    lineHeight: "35px",
    borderRadius: "20px",
  };
  const currentFileRef = useRef();
  const [mainModelsHistory, setMainModelsHistory] = useState(null);
  const [backgroundModelsHistory, setBackgroundModelsHistory] = useState(null);
  const [mainLatestFile, setMainLatestFile] = useState([]);
  const [backLatestFile, setBackLatestFile] = useState([]);
  const [newMainWorkset, setNewMainWorkset] = useState("");
  const [uploadMainFilePopup, setUploadMainFilePopup] = useState(false);
  const [duplicateMarksWarning, setDuplicateMarksWarning] = useState();
  const [noMarkWarning, setNoMarkWarning] = useState();
  const [qtoIdNotExistWarning, setQtoIdNotExistWarning] = useState();
  const [isLoadingOperation, setIsLoadingOperation] = useState(false);

  const modelHistoryHead = [
    { id: "filename", label: "Archive File Name", align: "left" },
    { id: "fileSize", label: "Size", align: "left" },
    {
      id: "uploadDate",
      label: "Upload Date",
      align: "left",
      type: "dateWithTime",
    },
    { id: "uploadBy", label: "Upload By", align: "left" },
    { id: "icon", label: "", align: "center" },
  ];

  const uploadLargeFileToBlob = async (projectId, file, showError) => {
    try {
      const sasResult = await getModelSASApi(projectId, file.name);
      if (!sasResult || !sasResult.data || !sasResult.data.filename || !sasResult.data.url) {
        showError("Failed to get SAS, cannot upload large file.");
        return { success: false };
      }
      await axios.put(sasResult.data.url, file, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/octet-stream'
        },
        maxBodyLength: Infinity
      });
      return { success: true, filename: sasResult.data.filename };
    } catch (error) {
      if (error?.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError("Large file upload to blob storage failed.");
      }
      return { success: false };
    }
  };

  const validateUploadedModel = (event, index, upload) => {
    const files = event.target.files;
    if (files.length === 0) return;

    const reader = new FileReader();
    reader.onload = function (e) {
      const fileContent = e.target.result;
      const elements = parseIfcFile(fileContent);
      const validateResult = validateModel(elements, qtoIds);

      setDuplicateMarksWarning("");
      setNoMarkWarning("");

      let hasError = false;

      if (validateResult.duplicateMarks.length > 0) {
        setDuplicateMarksWarning(
          <>
            <Typography fontWeight="bold">Following Mark(s) are duplicated in the model:</Typography>
            <Typography>{validateResult.duplicateMarks.join(", ")}.</Typography>
            <Typography fontWeight="bold" sx={{ py: 1 }}>
              Please correct them before upload.
            </Typography>
          </>
        );
        hasError = true;
      }

      if (validateResult.noMark.length > 0) {
        const noMarkMessage = (
          <>
            <Typography fontWeight="bold">Following element(s) have no Mark provided:</Typography>
            <Typography>{validateResult.noMark.join(", ")}.</Typography>
            {!hasError && validateResult.qtoIdNotExist.length === 0 && (
              <Typography fontWeight="bold" sx={{ py: 1 }}>
                Element ID will be used as Mark. Do you want to continue uploading?
              </Typography>
            )}
          </>
        );

        setNoMarkWarning(noMarkMessage);
        if (!hasError) {
          currentFileRef.current = { file: files[0], index };
        }
      }
      if (validateResult.qtoIdNotExist.length > 0) {
        const qtoIdNotExistMessage = (
          <>
            <Typography fontWeight="bold">Following QTO ID is not in project:</Typography>
            <Typography>{validateResult.qtoIdNotExist.join(", ")}.</Typography>
            {!hasError && validateResult.noMark.length === 0 && (
              <Typography fontWeight="bold" sx={{ py: 1 }}>
                Do you want to continue uploading?
              </Typography>
            )}
            {!hasError && validateResult.noMark.length > 0 && (
              <Typography fontWeight="bold" sx={{ py: 1 }}>
                Element ID will be used as Mark. Do you want to continue uploading?
              </Typography>
            )}
          </>
        );

        setQtoIdNotExistWarning(qtoIdNotExistMessage);
        if (!hasError) {
          currentFileRef.current = { file: files[0], index };
        }
      }
      if (validateResult.duplicateMarks.length > 0 || validateResult.noMark.length > 0 || validateResult.qtoIdNotExist.length > 0) {
        event.target.value = null;
        return;
      }
      upload(files[0], index);
    };

    reader.readAsText(files[0]);
  };

  const uploadNewMainModel = async (file) => {
    setIsLoadingOperation(true);
    setDuplicateMarksWarning();
    setNoMarkWarning();
    setQtoIdNotExistWarning();
    if (file.size > 10 * 1024 * 1024) {
      try {
        const uploadResult = await uploadLargeFileToBlob(projectId, file, showError);
        if (!uploadResult.success) {
          setIsLoadingOperation(false);
          return;
        }
        const result = await updateLargeMainModelApi(projectId, newMainWorkset, uploadResult.filename);
        if (result) {
          showSuccess("The large model file has been successfully uploaded to the project.");
          setUploadMainFilePopup(false);
          setNewMainWorkset("");
          await getMainTableData();
          await getCurrentMainModel();
          onUploadSuccess();
        } else {
          showError("Large file upload failed.");
        }
      } catch (error) {
        if (error?.response?.data?.detail) {
          showError(error.response.data.detail);
        } else {
          showError("Large file upload failed.");
        }
      } finally {
        setIsLoadingOperation(false);
      }
    } else {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const result = await updateMainModelApi(projectId, newMainWorkset, formData);
        if (result) {
          showSuccess("The model has been successfully uploaded to the project.");
          setUploadMainFilePopup(false);
          setNewMainWorkset("");
          await getMainTableData();
          await getCurrentMainModel();
          onUploadSuccess();
        } else {
          showError("Upload failed.");
        }
      } catch (error) {
        if (error?.response?.data?.detail) {
          showError(error.response.data.detail);
        } else {
          showError("Upload failed.");
        }
      } finally {
        setIsLoadingOperation(false);
      }
    }
  };

  const uploadNewMainVersion = async (file, index) => {
    setIsLoadingOperation(true);
    setDuplicateMarksWarning();
    setNoMarkWarning();
    setQtoIdNotExistWarning();
    const workset = mainLatestFile[index]?.workset;
    if (file.size > 10 * 1024 * 1024) {
      try {
        const uploadResult = await uploadLargeFileToBlob(projectId, file, showError);
        if (!uploadResult.success) {
          setIsLoadingOperation(false);
          return;
        }
        const result = await updateLargeMainModelApi(projectId, workset, uploadResult.filename);
        if (result) {
          showSuccess("The large model file has been successfully uploaded to the project.");
          await getMainTableData();
          await getCurrentMainModel();
          onUploadSuccess();
        } else {
          showError("Large file upload failed.");
        }
      } catch (error) {
        if (error?.response?.data?.detail) {
          showError(error.response.data.detail);
        } else {
          showError("Large file upload failed.");
        }
      } finally {
        setIsLoadingOperation(false);
      }
    } else {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const result = await updateMainModelApi(projectId, workset, formData);
        if (result) {
          showSuccess("The model has been successfully uploaded to the project.");
          await getMainTableData();
          await getCurrentMainModel();
          onUploadSuccess();
        } else {
          showError("Upload failed.");
        }
      } catch (error) {
        if (error?.response?.data?.detail) {
          showError(error.response.data.detail);
        } else {
          showError("Upload failed.");
        }
      } finally {
        setIsLoadingOperation(false);
      }
    }
  };

  const removeMainModel = async (event, index) => {
    setIsLoadingOperation(true);
    const modelId = event?.modelId;
    try {
      const result = await deleteMainModelApi(projectId, modelId);

      if (result) {
        showSuccess("The model has been removed from the project.");
        setMainLatestFile((prevModels) => {
          const updatedFileUploads = [...prevModels];
          updatedFileUploads.splice(index, 1);
          return updatedFileUploads;
        });
        onUploadSuccess();
      } else {
        showError("Remove failed.");
      }
    } catch {
      showError("Remove failed.");
    } finally {
      setIsLoadingOperation(false);
    }
  };

  const uploadNewBackgroundModel = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const file = files[0];
      setIsLoadingOperation(true);
      if (file.size > 10 * 1024 * 1024) {
        try {
          const uploadResult = await uploadLargeFileToBlob(projectId, file, showError);
          if (!uploadResult.success) {
            setIsLoadingOperation(false);
            return;
          }
          const result = await updateLargeBackgroundModelApi(projectId, uploadResult.filename);
          if (result) {
            showSuccess("The large background model file has been successfully uploaded to the project.");
            await getBackgroundTableData();
            await getCurrentBackModel();
            onUploadSuccess();
          } else {
            showError("Large file upload failed.");
          }
        } catch (error) {
          if (error?.response?.data?.detail) {
            showError(error.response.data.detail);
          } else {
            showError("Large file upload failed.");
          }
        } finally {
          setIsLoadingOperation(false);
        }
      } else {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const result = await updateBackgroundModelApi(projectId, formData);
          if (result) {
            showSuccess("The model has been successfully uploaded to the project.");
            await getBackgroundTableData();
            await getCurrentBackModel();
            onUploadSuccess();
          } else {
            showError("Upload failed.");
          }
        } catch (error) {
          if (error?.response?.data?.detail) {
            showError(error.response.data.detail);
          } else {
            showError("Upload failed.");
          }
        } finally {
          setIsLoadingOperation(false);
        }
      }
    }
  };

  const uploadNewBackVersion = async (event, index, type) => {
    const files = event.target.files;
    if (files.length > 0) {
      const file = files[0];
      setIsLoadingOperation(true);
      const modelId = backLatestFile[index]?.modelId;
      if (file.size > 10 * 1024 * 1024) {
        try {
          const uploadResult = await uploadLargeFileToBlob(projectId, file, showError);
          if (!uploadResult.success) {
            setIsLoadingOperation(false);
            return;
          }
          const result = await updateLargeBackgroundModelApi(projectId, uploadResult.filename, modelId);
          if (result) {
            showSuccess("The large background model file has been successfully uploaded to the project.");
            await getBackgroundTableData();
            await getCurrentBackModel();
            onUploadSuccess();
          } else {
            showError("Large file upload failed.");
          }
        } catch (error) {
          if (error?.response?.data?.detail) {
            showError(error.response.data.detail);
          } else {
            showError("Large file upload failed.");
          }
        } finally {
          setIsLoadingOperation(false);
        }
      } else {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const result = await updateBackgroundModelApi(projectId, formData, modelId);
          if (result) {
            showSuccess("The model has been successfully uploaded to the project.");
            await getBackgroundTableData();
            await getCurrentBackModel();
            onUploadSuccess();
          } else {
            showError("Upload failed.");
          }
        } catch (error) {
          if (error?.response?.data?.detail) {
            showError(error.response.data.detail);
          } else {
            showError("Upload failed.");
          }
        } finally {
          setIsLoadingOperation(false);
        }
      }
    }
  };

  const removeBackModel = async (event, index) => {
    setIsLoadingOperation(true);
    const modelId = event?.modelId;
    try {
      const result = await deleteBackgroundModelApi(projectId, modelId);
      if (result) {
        showSuccess("The model has been removed from the project.");
        setBackLatestFile((prevModels) => {
          const updatedFileUploads = [...prevModels];
          updatedFileUploads.splice(index, 1);
          return updatedFileUploads;
        });
        onUploadSuccess();
      } else {
        showError("Remove failed.");
      }
    } catch {
      showError("Remove failed.");
    } finally {
      setIsLoadingOperation(false);
    }
  };

  const downloadFile = (item) => {
    setIsLoadingOperation(true);
    const link = document.createElement("a");
    link.href = item?.fileURL;
    link.download = item?.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("The file has been successfully downloaded.");
      setIsLoadingOperation(false);
    }, 3000);
  };

  const getCurrentMainModel = useCallback(async () => {
    try {
      const result = await getMainModelDataApi(projectId);
      const data = result?.data;
      if (!data || !Array.isArray(data)) {
        showError("Received main model occurred error.");
        return;
      }
      setMainLatestFile(data);
    } catch {
      showError("Received main model occurred error.");
    }
  }, []);

  const getCurrentBackModel = useCallback(async () => {
    try {
      const result = await getBackgroundModelUrlApi(projectId);
      const data = result?.data;
      if (!data || !Array.isArray(data)) {
        showError("Received background model occurred error.");
        return;
      }
      setBackLatestFile(data);
    } catch {
      showError("Received background model occurred error.");
    }
  }, []);

  const getMainTableData = useCallback(async () => {
    try {
      const result = await getMainModelHistoryApi(projectId);
      const data = result?.data;

      if (!data || !Array.isArray(data)) {
        showError("Received main table data occurred error.");
        return;
      }

      const formattedData = data.map((item) => ({
        ...item,
        uploadDate: dayjs(item.uploadDate).format("YYYY-MM-DD HH:mm"),
        fileSize: getFileSize(item?.fileSize),
      }));

      setMainModelsHistory(formattedData);
    } catch {
      showError("Received main table data occurred error.");
    }
  }, []);

  const getBackgroundTableData = useCallback(async () => {
    try {
      const result = await getBackgroundModelHistoryApi(projectId);
      const data = result?.data;

      if (!data || !Array.isArray(data)) {
        showError("Received background table data occurred error.");
        return;
      }

      const formattedData = data.map((item) => ({
        ...item,
        uploadDate: dayjs(item.uploadDate).format("YYYY-MM-DD HH:mm"),
        fileSize: getFileSize(item?.fileSize),
      }));

      setBackgroundModelsHistory(formattedData);
    } catch {
      showError("Received background table data occurred error.");
    }
  }, []);

  useEffect(() => {
    getCurrentMainModel();
  }, [getCurrentMainModel]);

  useEffect(() => {
    getCurrentBackModel();
  }, [getCurrentBackModel]);

  useEffect(() => {
    getMainTableData();
  }, [getMainTableData]);

  useEffect(() => {
    getBackgroundTableData();
  }, [getBackgroundTableData]);

  return (
    <>
      <Popup
        open={open}
        onClose={onClose}
        title="Manage Models"
        popupStyle={{
          minWidth: 1000,
          maxWidth: 1000,
          maxHeight: "calc(95vh - 80px)",
        }}
      >
        <Box>
          <Box
            sx={{
              mx: 4,
              my: 2,
              p: 2,
              background: "#F5F5F5",
              borderRadius: "10px",
            }}
          >
            <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
              Main Project Models
            </Typography>
            <Typography component="span">&nbsp;&nbsp;* Model files shall be in IFC format</Typography>
            <Divider variant="fullWidth" />
            <Box sx={{ display: "flex", mb: 1, gap: 3 }}>
              <Typography component="span" sx={{ pt: 1.5 }}>
                Current:
              </Typography>
              <FileList
                files={mainLatestFile}
                accept=".ifc"
                onRemove={(event, index) => removeMainModel(event, index)}
                onUpload={(event, index, type) => validateUploadedModel(event, index, uploadNewMainVersion)}
                type="newMainVersion"
                buttonStyle={buttonStyle}
                onDownload={downloadFile}
                disabled={!user?.editRight}
              />
            </Box>
            <Table
              columns={modelHistoryHead}
              rows={mainModelsHistory}
              iconColumnName="icon"
              headerStyles={{
                default: { backgroundColor: "#D0D5DE" },
              }}
              iconImage={"assets/icons/clonedownload.svg"}
              columnWidths={{
                icon: "100px",
                fileSize: "100px",
                uploadDate: "120px",
                uploadBy: "130px",
              }}
              onHistoryUploadSuccess={() => showSuccess("The file has been successfully downloaded.")}
              sx={{ maxHeight: 130 }}
            />
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={2}>
              <UploadNewFileButton
                disabled={!user?.editRight}
                accept=".ifc"
                buttonProps={{
                  onClick: () => {
                    setUploadMainFilePopup(true);
                  },
                }}
              ></UploadNewFileButton>
            </Box>
          </Box>
          <Box
            sx={{
              mx: 4,
              my: 2,
              p: 2,
              background: "#F5F5F5",
              borderRadius: "10px",
            }}
          >
            <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
              Background Models
            </Typography>
            <Typography component="span">&nbsp;&nbsp;* Model files shall be in IFC, FBX format</Typography>
            <Divider variant="fullWidth" />
            <Box sx={{ display: "flex", mb: 1, gap: 3 }}>
              <Typography component="span" sx={{ pt: 1.5 }}>
                Current:
              </Typography>
              <FileList
                files={backLatestFile}
                accept=".ifc,.fbx"
                onRemove={(event, index) => removeBackModel(event, index)}
                onUpload={(event, index, type) => uploadNewBackVersion(event, index, type)}
                type="newBackVerison"
                buttonStyle={buttonStyle}
                onDownload={downloadFile}
                disabled={!user?.editRight}
              />
            </Box>
            <Table
              columns={modelHistoryHead}
              rows={backgroundModelsHistory}
              iconColumnName="icon"
              headerStyles={{
                default: { backgroundColor: "#D0D5DE" },
              }}
              iconImage={"assets/icons/clonedownload.svg"}
              columnWidths={{
                icon: "100px",
                fileSize: "100px",
                uploadDate: "120px",
                uploadBy: "130px",
              }}
              onHistoryUploadSuccess={() => showSuccess("The file has been successfully downloaded.")}
              sx={{ maxHeight: 130 }}
            />
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={2}>
              <UploadNewFileButton onChange={uploadNewBackgroundModel} disabled={!user?.editRight} accept=".ifc,.fbx"></UploadNewFileButton>
            </Box>
          </Box>
        </Box>
        <Box textAlign="center">
          <Button variant="outlined" color="inherit" onClick={onClose} sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}>
            Back
          </Button>
        </Box>
      </Popup>
      <Popup
        open={uploadMainFilePopup}
        onClose={() => setUploadMainFilePopup(false)}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        title="Upload New File"
      >
        <Box
          height={100}
          width={300}
          overflow="auto"
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ m: 4, alignItems: "center", justifyContent: "center" }}
        >
          <Box display="flex" gap={2} alignItems={"center"}>
            <Typography component="span">Workset</Typography>
            <TextField
              id="outlined-size-small"
              placeholder="Please enter workset..."
              variant="outlined"
              size="small"
              value={newMainWorkset}
              onChange={(event) => setNewMainWorkset(event.target.value)}
            />
          </Box>
          <UploadNewFileButton
            accept=".ifc"
            onChange={(event) => validateUploadedModel(event, undefined, uploadNewMainModel)}
            disabled={!user?.editRight || newMainWorkset === ""}
          ></UploadNewFileButton>
        </Box>
      </Popup>
      <NotificationPopup
        open={duplicateMarksWarning || noMarkWarning}
        onClose={() => {
          setDuplicateMarksWarning("");
          setNoMarkWarning("");
          setQtoIdNotExistWarning("");
        }}
        onProceed={
          duplicateMarksWarning
            ? undefined
            : newMainWorkset
              ? () => {
                uploadNewMainModel(currentFileRef.current.file);
              }
              : () => {
                uploadNewMainVersion(currentFileRef.current.file, currentFileRef.current.index);
              }
        }
        title="Warning"
        message={
          <>
            {duplicateMarksWarning}
            {noMarkWarning}
            {qtoIdNotExistWarning}
          </>
        }
        iconSrc="assets/icons/error.svg"
        proceedButtonLabel="Continue Upload"
        buttonLabel="Cancel"
        variant={"error"}
      />
      <FullScreenLoading isLoading={isLoadingOperation} />
    </>
  );
};

export default ManageModelPopup;
